// @flow strict

import * as React from 'react'
import cx from 'classnames'
import {
  BusinessButton,
  AccessibilityText,
  Tooltip,
} from '@mondough/public-web-components'
import { prettyPrintPriceFromMinor } from '@mondough/public-web-components/src/utils/currencyUtils'

import { CheckMark, Cross } from './PricingTable'

import { type PricingTableProps } from './PricingTable'
import styles from './MobilePricingTable.module.scss'
import { SectionWrapper, SectionThemes } from '../../../SectionWrapper'

export const MobilePricingTable = ({
  featureGroups,
  plans,
  title,
  signupLink,
}: PricingTableProps) => {
  const [
    currentlyDisplayedTooltip,
    setCurrentlyDisplayedTooltip,
  ] = React.useState('')
  // On mobile all features are shown in one large list, so we spread them here
  // in to a single array
  const features = []
  featureGroups.map(group => {
    return features.push(...group.features)
  })

  return (
    <SectionWrapper
      theme={SectionThemes.LIGHT}
      contentClassName={styles.baseContent}
      className={styles.section}
    >
      {title && <h2 className={styles.title}>{title}</h2>}
      <div className={styles.root}>
        <table className={styles.table}>
          <tbody>
            <tr>
              <th
                scope="col"
                className={cx(styles.featureTitleCell, styles.headerCell)}
              >
                Features
              </th>
              <th scope="col" className={styles.headerCell}>
                Pro
              </th>
              <th scope="col" className={styles.headerCell}>
                Lite
              </th>
            </tr>
            <tr>
              <th
                className={cx(
                  styles.featureTitleCell,
                  styles.pricePerMonthTitle,
                )}
                scope="row"
              >
                Monthly price
              </th>
              {plans.map(({ id, pricePerMonthInPence }) => (
                <td key={`price_${id}`} className={styles.pricePerMonth}>
                  {pricePerMonthInPence === 0
                    ? 'Free'
                    : `£${prettyPrintPriceFromMinor(pricePerMonthInPence)}`}
                </td>
              ))}
            </tr>

            {features.map((feature, featureIndex) => {
              const featureKey = feature.title.replace(' ', '')
              const tooltipId = `mob-${featureKey}-tooltip`
              return (
                <tr key={`mob_${featureKey}`}>
                  <th scope="row" className={styles.featureTitleCell}>
                    <span className={styles.featureTitle}>
                      {feature.title}
                      <Tooltip
                        className={styles.tooltipIcon}
                        id={tooltipId}
                        label="More information"
                        description={feature.description}
                        onClick={() => {
                          currentlyDisplayedTooltip === tooltipId
                            ? setCurrentlyDisplayedTooltip('')
                            : setCurrentlyDisplayedTooltip(tooltipId)
                        }}
                        showTooltip={currentlyDisplayedTooltip === tooltipId}
                        flipped={featureIndex >= features.length - 2}
                        link={
                          feature.tooltipLink != null
                            ? feature.tooltipLink
                            : undefined
                        }
                        linkText={feature.tooltipLinkText || ''}
                      />
                    </span>
                  </th>
                  {plans.map(plan => (
                    <td key={`mob_${plan.id}_${featureKey}`}>
                      {feature.plans.includes(plan.id) ? (
                        <>
                          <CheckMark />
                          <AccessibilityText>Yes</AccessibilityText>
                        </>
                      ) : (
                        <>
                          <Cross />
                          <AccessibilityText>No</AccessibilityText>
                        </>
                      )}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
        <div className={styles.buttonWrapper}>
          <BusinessButton href={signupLink} className={styles.businessButton}>
            Get a Business account
          </BusinessButton>
        </div>
      </div>
    </SectionWrapper>
  )
}

export default MobilePricingTable
